import React from 'react';
import { TAnnotatedDataPoint } from '@@config/report';
import Tooltip from '@@components/Tooltip/Tooltip';
import { renderInscriptionWithImages } from '@@utils/utils';

type TStructuredGridTableRowProps = {
    item: TAnnotatedDataPoint;
    index: number;
    currentTooltipIndex: number;
    totalRows: number;
    onNextTooltipClick: () => void;
    resetTooltips: () => void;
    showTooltipByIndex: (index: number) => void;
};

const StructuredGridTableRow: React.FC<TStructuredGridTableRowProps> = ({
    item,
    index,
    currentTooltipIndex,
    totalRows,
    onNextTooltipClick,
    resetTooltips,
    showTooltipByIndex,
}) => {
    // Determine if flex layout should be used based on KIND
    const shouldUseFlex = item.KIND === 'image';

    // Handle different KIND types
    const renderValue = () => {
        switch (item.KIND) {
            case 'image':
                return (
                    <img
                        className='block max-w-full h-auto w-1/2 mx-auto my-2'
                        src={item.VALUE}
                        alt={item.LABEL || 'Report image'}
                    />
                );
            case 'inscription_embedded_image_string': {
                const images = item.SUPPLEMENTAL?.INSCRIPTION_GRAPHICS || [];
                const keyPrefix = item.KEY || `inscription-${index}`;
                return <>{renderInscriptionWithImages(item.VALUE, images, keyPrefix)}</>;
            }
            default:
                return <span className='font-bold uppercase'>{item.VALUE}</span>;
        }
    };

    // Build className for value cell based on KIND
    const getValueCellClassNames = () => {
        const baseClasses = 'xtable-cell relative';
        if (shouldUseFlex) {
            return `${baseClasses} flex flex-col`;
        }
        return baseClasses;
    };

    return (
        <div className='xtable-row' style={{ gridTemplateColumns: `repeat(2, 1fr)` }}>
            {item.LABEL ? (
                // Two-column layout: Label in first column, value in second
                <>
                    <div className='xtable-cell relative'>
                        <span>{item.LABEL}</span>
                        {index >= 0 && index === currentTooltipIndex && item.TOOLTIP && (
                            <Tooltip
                                title={item.LABEL}
                                content={item.TOOLTIP}
                                tooltipIndex={index}
                                totalSlides={totalRows}
                                onNextClick={onNextTooltipClick}
                                resetTooltips={resetTooltips}
                                showTooltipByIndex={showTooltipByIndex}
                            />
                        )}
                    </div>
                    <div className={getValueCellClassNames()}>{renderValue()}</div>
                </>
            ) : (
                // Full-width layout: Value spans both columns with special styling
                <div className='xtable-cell flex relative' style={{ gridColumn: '1 / -1' }}>
                    {renderValue()}
                </div>
            )}
        </div>
    );
};

export type { TStructuredGridTableRowProps };
export default StructuredGridTableRow;
